/* eslint-disable */
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Modal, Divider, Tooltip, Spin, Tag } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ShopOutlined, UserOutlined, MailOutlined, PhoneOutlined,
  EnvironmentOutlined, LinkOutlined, GlobalOutlined,
  PlusOutlined, DeleteOutlined, BankOutlined, IdcardOutlined,
  CheckCircleFilled, SearchOutlined,
} from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPost } from '../../../store/http';
import { hideLoader, showLoader } from '../../../utils/loader';
import FilterSearchMerchant from '../../../pos/components/PosComponents/FilterSearchMerchant';
import '../index.scss';

/* ── Styles ─────────────────────────────────────────────────────────── */
const styles = {
  overlay: {
    background: 'linear-gradient(135deg,#ff6700 0%,#e65c00 60%,#cc5200 100%)',
    borderRadius: '16px 16px 0 0',
    padding: '24px 28px 20px',
    margin: '-20px -24px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    position: 'relative',
    overflow: 'hidden',
  },
  overlayBefore: {
    position: 'absolute',
    top: '-50%', right: '-8%',
    width: 220, height: 220,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.07)',
    pointerEvents: 'none',
  },
  iconBox: {
    width: 48, height: 48,
    borderRadius: 12,
    background: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255,255,255,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, color: '#fff', flexShrink: 0,
  },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: 800, margin: 0, letterSpacing: '-0.3px' },
  headerSub: { color: 'rgba(255,255,255,0.78)', fontSize: 12, margin: 0, fontWeight: 500, marginTop: 3 },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px',
    color: '#ff6700', marginBottom: 14, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6,
  },
  label: {
    fontSize: 11, fontWeight: 600, color: '#595959',
    textTransform: 'uppercase', letterSpacing: '0.4px',
    display: 'block', marginBottom: 5,
  },
  inputWrap: { position: 'relative', marginBottom: 0 },
  inputIcon: {
    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
    color: '#bbb', fontSize: 14, pointerEvents: 'none', zIndex: 1,
  },
  input: {
    width: '100%', height: 40, paddingLeft: 36, paddingRight: 12,
    border: '1.5px solid #e8e8e8', borderRadius: 10, fontSize: 13,
    background: '#fafafa', color: '#1a1a1a', fontFamily: 'inherit',
    outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  },
  inputDisabled: {
    background: '#f5f5f5', color: '#595959', cursor: 'default',
  },
  inputFocus: { borderColor: '#ff6700', boxShadow: '0 0 0 2px rgba(255,103,0,0.12)', background: '#fff' },
  errorMsg: { color: '#ef4444', fontSize: 11, marginTop: 3, fontWeight: 500 },
  fieldGroup: { marginBottom: 18 },
  submitBtn: {
    width: '100%', height: 48,
    background: 'linear-gradient(135deg,#ff6700,#e65c00)',
    color: '#fff', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.2s', letterSpacing: 0.3,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  addIpBtn: {
    background: 'none', border: '1.5px dashed #ff6700', borderRadius: 10,
    color: '#ff6700', fontSize: 13, fontWeight: 600, padding: '8px 16px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    transition: 'background 0.2s',
  },
  removeIpBtn: {
    background: '#fff', border: '1.5px solid #fecaca', borderRadius: 8,
    color: '#ef4444', cursor: 'pointer', padding: '6px 10px',
    display: 'flex', alignItems: 'center', fontSize: 13,
    transition: 'background 0.2s', flexShrink: 0,
  },
  ipRow: { display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 },
  previewCard: {
    background: 'linear-gradient(135deg,#fff8f4,#fff)',
    border: '1.5px solid #ffe0c8', borderRadius: 14,
    padding: '16px 20px', marginBottom: 20,
  },
  previewGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginTop: 10,
  },
  previewItem: { display: 'flex', flexDirection: 'column' },
  previewLabel: { fontSize: 10, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.4px' },
  previewValue: { fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginTop: 2 },
  divider: { borderColor: '#f0f0f0', margin: '18px 0' },
  selectWrap: { position: 'relative' },
};

/* ── Controlled input with focus ring ──────────────────────────────── */
const FormField = ({ label, icon, error, touched, disabled, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={styles.fieldGroup}>
      {label && <span style={styles.label}>{label}</span>}
      <div style={styles.inputWrap}>
        {icon && <span style={styles.inputIcon}>{icon}</span>}
        <input
          {...props}
          disabled={disabled}
          style={{
            ...styles.input,
            paddingLeft: icon ? 36 : 14,
            ...(disabled ? styles.inputDisabled : {}),
            ...(focused && !disabled ? styles.inputFocus : {}),
          }}
          onFocus={() => setFocused(true)}
          onBlur={(e) => { setFocused(false); props.onBlur && props.onBlur(e); }}
        />
      </div>
      {touched && error && <div style={styles.errorMsg}>⚠ {error}</div>}
    </div>
  );
};

/* ── Main Component ─────────────────────────────────────────────────── */
const CreateSingleUserModal = ({ showModal, hideModal, title, className, reload, setReload }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [hoveredSubmit, setHoveredSubmit] = useState(false);

  const getMerchantBankDetails = async (userId) => {
    if (!userId) return;
    setWalletsLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.authUrl}/api/v1/accounts/user/${userId}/details`);
      if (res.data && Array.isArray(res.data.accounts)) {
        setWallets(res.data.accounts);
      } else {
        setWallets([]);
      }
    } catch {
      setWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    showLoader();
    const payload = {
      accountHolderName: values.accountHolderName,
      address: values.address,
      companyName: values.companyName,
      email: values.email,
      ipAddresses: values.ipAddresses.filter(ip => ip.trim() !== ''),
      merchantId: values.merchantId,
      phoneNumber: values.phoneNumber,
      wayaBankAccountNumber: values.wayaBankAccountNumber,
      webhookUrl: values.webhookUrl,
      ownerEmail: values.ownerEmail,
      firstName: values.firstName,
      lastName: values.lastName,
    };
    const res = await httpPost(`${customBaseUrl.integUrl}/api/v1/merchants/register`, payload);
    if (res.status) {
      hideLoader();
      Swal.fire('Done!', res.message, 'success');
      setReload(!reload);
      hideModal(false);
    } else {
      hideLoader();
      Swal.fire('Error', res.message, 'error');
    }
  };

  const formik = useFormik({
    initialValues: {
      accountHolderName: '',
      address: '',
      companyName: '',
      phoneNumber: '',
      wayaBankAccountNumber: '',
      merchantId: '',
      email: '',
      ownerEmail: '',
      firstName: '',
      lastName: '',
      webhookUrl: '',
      ipAddresses: [''],
    },
    validationSchema: Yup.object({
      accountHolderName: Yup.string().required('Account holder name is required'),
      companyName: Yup.string().required('Company name is required'),
      wayaBankAccountNumber: Yup.string().max(10, 'Must be 10 characters or less').required('Required'),
      merchantId: Yup.string().required('Merchant ID is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      ownerEmail: Yup.string().email('Invalid email address').required('Owner email is required'),
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      webhookUrl: Yup.string().notRequired(),
      ipAddresses: Yup.array().of(
        Yup.string().trim().matches(
          /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
          'Invalid IP address format'
        ).notRequired()
      ),
    }),
    onSubmit: handleSubmit,
  });

  /* Auto-fill form when merchant is selected */
  useEffect(() => {
    if (selectedValue && selectedValue.length > 0) {
      const merchant = selectedValue[0];
      getMerchantBankDetails(merchant.userId ?? merchant.id);
      formik.setValues({
        ...formik.values,
        companyName: merchant.orgName || merchant?.businessName || '',
        email: merchant.email || '',
        phoneNumber: merchant.phoneNumber || '',
        address: merchant.officeAddress || merchant?.streetAddress || '',
        accountHolderName: `${merchant.firstName || ''} ${merchant.lastName || ''}`.trim(),
        merchantId: merchant.merchantId || '',
        wayaBankAccountNumber: merchant.wayaBankAccountNumber || '',
        firstName: merchant.firstName || '',
        lastName: merchant.lastName || '',
        ownerEmail: merchant.email || '',
      });
    }
  }, [selectedValue]);

  const hasMerchantSelected = selectedValue && selectedValue.length > 0;

  return (
    <Modal
      className={className}
      centered
      open={showModal}
      onOk={() => hideModal(false)}
      onCancel={() => hideModal(false)}
      width={860}
      footer={null}
      bodyStyle={{ padding: '20px 24px 24px', fontFamily: "'Inter', -apple-system, sans-serif" }}
      title={null}
    >
      {/* ── Premium Header ── */}
      <div style={styles.overlay}>
        <div style={styles.overlayBefore} />
        <div style={styles.iconBox}><ShopOutlined /></div>
        <div>
          <p style={styles.headerTitle}>Onboard New Business</p>
          <p style={styles.headerSub}>Search for an existing WayaBank merchant to register them on B2B</p>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        {/* ── Merchant Search ── */}
        <div style={styles.sectionTitle}>
          <SearchOutline />
          Step 1 — Find Merchant
        </div>
        <div style={{
          background: '#fafafa', borderRadius: 12, padding: '16px 18px',
          border: '1.5px solid #f0f0f0', marginBottom: 20,
        }}>
          <FilterSearchMerchant setSelectedValue={setSelectedValue} />
        </div>

        {/* ── Merchant Preview Card (shown when selected) ── */}
        {hasMerchantSelected && (
          <div style={styles.previewCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <CheckCircleFilled style={{ color: '#22c55e', fontSize: 16 }} />
              <span style={{ fontWeight: 700, color: '#1a1a1a', fontSize: 14 }}>Merchant found</span>
              <Tag style={{
                background: '#fff3eb', color: '#ff6700', border: '1px solid #ffbb96',
                borderRadius: 8, fontWeight: 600, fontSize: 11,
              }}>
                {formik.values.merchantId || 'N/A'}
              </Tag>
            </div>
            <div style={styles.previewGrid}>
              <div style={styles.previewItem}>
                <span style={styles.previewLabel}>Company</span>
                <span style={styles.previewValue}>{formik.values.companyName || '—'}</span>
              </div>
              <div style={styles.previewItem}>
                <span style={styles.previewLabel}>Account Holder</span>
                <span style={styles.previewValue}>{formik.values.accountHolderName || '—'}</span>
              </div>
              <div style={styles.previewItem}>
                <span style={styles.previewLabel}>Email</span>
                <span style={styles.previewValue}>{formik.values.email || '—'}</span>
              </div>
              <div style={styles.previewItem}>
                <span style={styles.previewLabel}>Phone</span>
                <span style={styles.previewValue}>{formik.values.phoneNumber || '—'}</span>
              </div>
            </div>
          </div>
        )}

        <Divider style={styles.divider} />

        {/* ── Form ── */}
        <div style={styles.sectionTitle}>
          <EditOutline />
          Step 2 — Confirm Details
        </div>

        <form onSubmit={formik.handleSubmit}>
          {/* Row 1: Company + Email + Phone */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 18px' }}>
            <FormField
              label="Company Name"
              icon={<ShopOutlined />}
              id="companyName"
              name="companyName"
              type="text"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.companyName}
              error={formik.errors.companyName}
              touched={formik.touched.companyName}
              placeholder="Auto-filled"
            />
            <FormField
              label="Company Email"
              icon={<MailOutlined />}
              id="email"
              name="email"
              type="email"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              error={formik.errors.email}
              touched={formik.touched.email}
              placeholder="Auto-filled"
            />
            <FormField
              label="Company Phone"
              icon={<PhoneOutlined />}
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phoneNumber}
              error={formik.errors.phoneNumber}
              touched={formik.touched.phoneNumber}
              placeholder="Auto-filled"
            />
          </div>

          {/* Row 2: Address + Account Holder */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0 18px' }}>
            <FormField
              label="Company Address"
              icon={<EnvironmentOutlined />}
              id="address"
              name="address"
              type="text"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address}
              error={formik.errors.address}
              touched={formik.touched.address}
              placeholder="Auto-filled"
            />
            <FormField
              label="Account Holder Name"
              icon={<UserOutlined />}
              id="accountHolderName"
              name="accountHolderName"
              type="text"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.accountHolderName}
              error={formik.errors.accountHolderName}
              touched={formik.touched.accountHolderName}
              placeholder="Auto-filled"
            />
          </div>

          {/* Row 3: First Name + Last Name + Merchant Key */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 18px' }}>
            <FormField
              label="First Name"
              icon={<UserOutlined />}
              id="firstName"
              name="firstName"
              type="text"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.firstName}
              error={formik.errors.firstName}
              touched={formik.touched.firstName}
              placeholder="Auto-filled"
            />
            <FormField
              label="Last Name"
              icon={<UserOutlined />}
              id="lastName"
              name="lastName"
              type="text"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.lastName}
              error={formik.errors.lastName}
              touched={formik.touched.lastName}
              placeholder="Auto-filled"
            />

            <FormField
              label="Owner's Email"
              icon={<MailOutlined />}
              id="ownerEmail"
              name="ownerEmail"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.ownerEmail}
              error={formik.errors.ownerEmail}
              touched={formik.touched.ownerEmail}
              placeholder=""
            />
          </div>

          {/* Row 4: WAYA Account Picker + Merchant ID + Owner's Email */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 18px' }}>
            {/* WAYA Account selector */}
            <div style={styles.fieldGroup}>
              <span style={styles.label}>Select WAYA Account</span>
              <div style={{ position: 'relative' }}>
                <span style={{ ...styles.inputIcon, top: 20 }}><BankOutlined /></span>
                {walletsLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, paddingLeft: 12 }}>
                    <Spin size="small" /> <span style={{ fontSize: 12, color: '#8c8c8c' }}>Loading wallets…</span>
                  </div>
                ) : (
                  <select
                    style={{
                      ...styles.input, paddingLeft: 36, appearance: 'none',
                      cursor: wallets.length ? 'pointer' : 'not-allowed',
                    }}
                    onChange={(e) => {
                      const w = wallets.find(w => w.accountNumber === e.target.value);
                      if (w) {
                        formik.setFieldValue('wayaBankAccountNumber', w.accountNumber);
                        formik.setFieldValue('accountHolderName', w.accountName);
                      } else {
                        formik.setFieldValue('wayaBankAccountNumber', e.target.value);
                      }
                    }}
                  >
                    <option value="">{wallets.length ? 'Choose account…' : 'Search merchant first'}</option>
                    {wallets.map((w) => (
                      <option key={w.accountNumber} value={w.accountNumber}>
                        {w.accountNumber} · {w.accountName} · ₦{Number(w.availableBalance || 0).toLocaleString()}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <FormField
              label="Merchant ID"
              icon={<IdcardOutlined />}
              id="merchantId"
              name="merchantId"
              type="text"
              disabled
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.merchantId}
              error={formik.errors.merchantId}
              touched={formik.touched.merchantId}
              placeholder="Auto-filled"
            />
          </div>

          {/* Webhook URL */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={styles.label}><LinkOutlined style={{ marginRight: 4 }} />Webhook URL (optional)</span>
            </div>
            <FormField
              icon={<LinkOutlined />}
              id="webhookUrl"
              name="webhookUrl"
              type="url"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.webhookUrl}
              error={formik.errors.webhookUrl}
              touched={formik.touched.webhookUrl}
              placeholder="https://yourapp.com/webhook"
            />
          </div>

          {/* IP Addresses */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={styles.label}><GlobalOutlined style={{ marginRight: 4 }} />IP Addresses (optional)</span>
            </div>
            {formik.values.ipAddresses.map((ip, index) => (
              <div key={index} style={styles.ipRow}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <span style={styles.inputIcon}><GlobalOutlined /></span>
                  <input
                    type="text"
                    name={`ipAddresses[${index}]`}
                    style={{ ...styles.input, paddingLeft: 36 }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={ip}
                    placeholder="e.g. 192.168.1.1"
                  />
                  {formik.touched.ipAddresses && formik.errors.ipAddresses && formik.errors.ipAddresses[index] && (
                    <div style={styles.errorMsg}>⚠ {formik.errors.ipAddresses[index]}</div>
                  )}
                </div>
                {formik.values.ipAddresses.length > 1 && (
                  <Tooltip title="Remove">
                    <button
                      type="button"
                      style={styles.removeIpBtn}
                      onClick={() => {
                        const arr = [...formik.values.ipAddresses];
                        arr.splice(index, 1);
                        formik.setFieldValue('ipAddresses', arr);
                      }}
                    >
                      <DeleteOutlined />
                    </button>
                  </Tooltip>
                )}
              </div>
            ))}
            <button
              type="button"
              style={styles.addIpBtn}
              onClick={() => formik.setFieldValue('ipAddresses', [...formik.values.ipAddresses, ''])}
            >
              <PlusOutlined /> Add IP Address
            </button>
          </div>

          {/* Submit */}
          <Divider style={{ ...styles.divider, marginTop: 8 }} />
          <Tooltip title={!hasMerchantSelected ? 'Search and select a merchant first' : ''}>
            <button
              type="submit"
              style={{
                ...styles.submitBtn,
                ...(hoveredSubmit && hasMerchantSelected ? {
                  background: 'linear-gradient(135deg,#e65c00,#cc5200)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(255,103,0,0.35)',
                } : {}),
                opacity: !hasMerchantSelected ? 0.55 : 1,
                cursor: !hasMerchantSelected ? 'not-allowed' : 'pointer',
              }}
              disabled={!hasMerchantSelected}
              onMouseEnter={() => setHoveredSubmit(true)}
              onMouseLeave={() => setHoveredSubmit(false)}
            >
              <ShopOutlined />
              Register Business
            </button>
          </Tooltip>
        </form>
      </div>
    </Modal>
  );
};

/* Inline SVG-style icon stand-ins for section headers */
const SearchOutline = () => <SearchOutlined style={{ fontSize: 12 }} />;
const EditOutline = () => <IdcardOutlined style={{ fontSize: 12 }} />;

export default CreateSingleUserModal;
